import React, { useState, useRef, useEffect, useContext } from "react";
import authFetch from "../../authFetch";
import PlatformCtx from "../../state/PlatformCtx";
import { TODAY, fmt, fmtSAR } from "../../utils/format";
import { userName, locName, localName } from "../../utils/helpers";
import { useValidationToast, validateFields } from "../../utils/validation";
import Modal from "../../shared/Modal";
import AIPanel from "../../shared/AIPanel";
import GroceryLabel, { useGroceryPrefetch } from "../../shared/GroceryLabel";
import { ExpenseForm } from "../finance/expenses/ExpensesModule";
import useBnavFab from "../../shared/useBnavFab";
import { getEffectiveTaxonomy } from "../../permissions/checks";

/* ══ HOUSEHOLD OPS ═════════════════════════════════════════ */
function HouseholdOps({state,dispatch,t}){
  return <HouseholdGroceriesTab state={state} dispatch={dispatch} t={t}/>;
}

function HouseholdGroceriesTab({state,dispatch,t}){
  const {groceries,users,locations,locFilter,lang,currentUser}=state;
  const grocConfig=state.groceryConfig||{};
  const [showBulkGrocery,setShowBulkGrocery]=useState(false);useBnavFab(()=>setShowBulkGrocery(true));
  const [showGroceryAnalytics,setShowGroceryAnalytics]=useState(true);
  const [grocExpModal,setGrocExpModal]=useState(null);
  const [grocDetailKey,setGrocDetailKey]=useState(null);
  const isRTL=lang==="ar";
  const API=typeof window!=="undefined"?window.location.origin:"";

  /* ── Unified status model (same as task engine) ── */
  const GROC_STATUS_LABEL={pending:isRTL?"معلق":"Pending",approved:isRTL?"موافق":"Approved",in_progress:isRTL?"في الطريق":"In Transit",completed:isRTL?"مكتمل":"Fulfilled",cancelled:isRTL?"ملغي":"Cancelled"};
  const GROC_STATUS_PILL={pending:"p-open",approved:"p-wait",in_progress:"p-wait",completed:"p-done",cancelled:"p-low"};
  const GROC_VALID_TRANSITIONS={pending:["in_progress","cancelled"],approved:["in_progress","cancelled"],in_progress:["completed","cancelled"],completed:[],cancelled:["pending"]};

  const getGrocStatus=(g)=>{
    const tk=(state.tasks||[]).find(t=>t.id===g.taskId);
    if(tk)return tk.status;
    /* Legacy fallback */
    const map={Pending:"pending","In Transit":"in_progress",Fulfilled:"completed"};
    return map[g.status]||g.status||"pending";
  };

  const fg=locFilter?groceries.filter(g=>!g.locId||g.locId===locFilter):groceries;

  /* ── Group grocery items by request (batch taskId); legacy unlinked items become single-item requests ── */
  const grocGroups=(()=>{
    const byKey=new Map();
    for(const g of fg){
      const key=g.taskId||`single:${g.id}`;
      if(!byKey.has(key))byKey.set(key,{key,taskId:g.taskId||null,items:[]});
      byKey.get(key).items.push(g);
    }
    const groups=Array.from(byKey.values()).map(gr=>{
      const batchTask=gr.taskId?(state.tasks||[]).find(tk=>tk.id===gr.taskId):null;
      const first=gr.items[0];
      const statuses=gr.items.map(getGrocStatus);
      let overallStatus;
      if(batchTask)overallStatus=batchTask.status;
      else if(statuses.every(s=>s==="completed"))overallStatus="completed";
      else if(statuses.every(s=>s==="cancelled"))overallStatus="cancelled";
      else if(statuses.some(s=>s==="in_progress"))overallStatus="in_progress";
      else if(statuses.some(s=>s==="pending"))overallStatus="pending";
      else overallStatus=statuses[0]||"pending";
      const doneCount=statuses.filter(s=>s==="completed").length;
      const totalCost=gr.items.reduce((s,g)=>s+(Number(g.totalCost||g.cost)||0),0);
      /* Group title — bilingual. Multi-item groups use a localised
         template; single-item groups defer to the item's own bilingual
         fields so GroceryLabel can render either side. */
      const multiTitleEn=`Grocery Request — ${gr.items.length} item${gr.items.length===1?"":"s"}`;
      const multiTitleAr=`طلب بقالة — ${gr.items.length} ${gr.items.length===1?"عنصر":"عناصر"}`;
      return {...gr,batchTask,first,overallStatus,doneCount,totalCost,
        title:batchTask?.title||(gr.items.length>1?multiTitleEn:(first.itemEn||first.item)),
        titleAr:batchTask?.titleAr||(gr.items.length>1?multiTitleAr:(first.itemAr||"")),
        date:first.date||batchTask?.createdAt?.slice(0,10)||"",
        locId:first.locId||batchTask?.locId||"",
        requestedBy:first.requestedBy||batchTask?.createdBy||"",
      };
    });
    /* Newest requests first */
    groups.sort((a,b)=>(b.date||"").localeCompare(a.date||""));
    return groups;
  })();

  /* ── Transition a grocery item (and its batch task) ── */
  const doGrocTransition=async(id,newStatus)=>{
    const g=groceries.find(x=>x.id===id);
    if(!g)return;

    /* If completing and expense required, check if this is the last item */
    if(newStatus==="completed"&&g.taskId){
      const batchItems=groceries.filter(x=>x.taskId===g.taskId&&x.id!==id);
      const allOthersDone=batchItems.every(x=>{const s=getGrocStatus(x);return s==="completed";});
      if(allOthersDone&&grocConfig.requireExpense){
        setGrocExpModal({id,item:g.item,taskId:g.taskId,isLastItem:true});
        return;
      }
    }

    /* Update grocery status */
    dispatch({type:"UPDATE_GROCERY",id,patch:{status:newStatus}});

    /* Sync batch task */
    if(g.taskId){
      if(newStatus==="in_progress"){
        /* First item moving to in_progress → move the task too */
        const tk=(state.tasks||[]).find(t=>t.id===g.taskId);
        if(tk&&(tk.status==="pending"||tk.status==="approved")){
          try{
            const res=await authFetch(`${API}/api/tasks?action=transition`,{method:"POST",headers:{"Content-Type":"application/json"},
              body:JSON.stringify({ws_key:state.wsKey,task_id:g.taskId,new_status:"in_progress",actor_id:currentUser?.id,actor_name:currentUser?.name})});
            const data=await res.json();
            if(data.ok)dispatch({type:"UPDATE_TASK",id:g.taskId,patch:data.patch});
          }catch(e){console.error("[Grocery] task transition failed",e);}
        }
      }
      if(newStatus==="completed"){
        /* Check if ALL items in batch are now done */
        const batchItems=groceries.filter(x=>x.taskId===g.taskId&&x.id!==id);
        const allOthersDone=batchItems.every(x=>{const s=getGrocStatus(x);return s==="completed";});
        if(allOthersDone){
          /* Complete the batch task */
          const tk=(state.tasks||[]).find(t=>t.id===g.taskId);
          if(tk&&tk.status!=="completed"&&tk.status!=="cancelled"){
            let cur=tk.status;
            /* Step to in_progress first if needed */
            if(cur==="pending"||cur==="approved"){
              try{
                const r=await authFetch(`${API}/api/tasks?action=transition`,{method:"POST",headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({ws_key:state.wsKey,task_id:g.taskId,new_status:"in_progress",actor_id:currentUser?.id,actor_name:currentUser?.name})});
                const d=await r.json();
                if(d.ok){dispatch({type:"UPDATE_TASK",id:g.taskId,patch:d.patch});cur="in_progress";}
              }catch(e){console.error(e);}
            }
            if(cur==="in_progress"){
              try{
                const r=await authFetch(`${API}/api/tasks?action=transition`,{method:"POST",headers:{"Content-Type":"application/json"},
                  body:JSON.stringify({ws_key:state.wsKey,task_id:g.taskId,new_status:"completed",actor_id:currentUser?.id,actor_name:currentUser?.name})});
                const d=await r.json();
                if(d.ok)dispatch({type:"UPDATE_TASK",id:g.taskId,patch:d.patch});
              }catch(e){console.error(e);}
            }
          }
        }
      }
    }
  };

  const fulfillWithExpense=async(id,expenseData)=>{
    const g=groceries.find(x=>x.id===id);
    if(!g)return;
    dispatch({type:"UPDATE_GROCERY",id,patch:{status:"completed"}});
    if(g.taskId){
      const tk=(state.tasks||[]).find(t=>t.id===g.taskId);
      if(tk&&tk.status!=="completed"){
        let cur=tk.status;
        if(cur==="pending"||cur==="approved"){
          try{const r=await authFetch(`${API}/api/tasks?action=transition`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:state.wsKey,task_id:g.taskId,new_status:"in_progress",actor_id:currentUser?.id,actor_name:currentUser?.name})});const d=await r.json();if(d.ok){dispatch({type:"UPDATE_TASK",id:g.taskId,patch:d.patch});cur="in_progress";}}catch(e){console.error(e);}
        }
        if(cur==="in_progress"){
          try{const r=await authFetch(`${API}/api/tasks?action=transition`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:state.wsKey,task_id:g.taskId,new_status:"completed",actor_id:currentUser?.id,actor_name:currentUser?.name,expense_data:expenseData})});const d=await r.json();if(d.ok)dispatch({type:"UPDATE_TASK",id:g.taskId,patch:d.patch});}catch(e){console.error(e);}
        }
      }
    }
  };

  /* Prefetch bilingual labels for all grocery groups + their items so
     GroceryLabel renders instantly from the localStorage cache. */
  useGroceryPrefetch(
    (grocGroups||[]).flatMap(gr => [
      {item:gr.title, itemAr:gr.titleAr},
      ...(gr.items||[]).map(g => ({item:g.item, itemAr:g.itemAr})),
    ]),
    lang
  );

  return(
    <div className="page inc-page-bg">
      {/* ═══ MOBILE: Hero + List ═══ */}
      <div className="hhs-hero">
        <div className="hhs-hero-greeting">{state.platformModuleNames?.Household||t.household}</div>
        <div className="hhs-hero-sub">{state.moduleSubtitles?.Household||`${locFilter?locName(locations,locFilter):(isRTL?"جميع المنازل":"All Homes")} · ${isRTL?"البقالة":"Groceries"}`}</div>
        {(()=>{
          const totalC=fg.length;
          const pendingC=fg.filter(g=>getGrocStatus(g)==="pending").length;
          const inProgC=fg.filter(g=>getGrocStatus(g)==="in_progress").length;
          const doneC=fg.filter(g=>getGrocStatus(g)==="completed").length;
          return(
          <div className="hhs-summary-glass">
            <div className="hhs-summary-row">
              <div className="hhs-summary-item"><div className="hhs-summary-val" style={{color:"var(--ink)"}}>{totalC}</div><div className="hhs-summary-lbl">{isRTL?"الإجمالي":"Total"}</div></div>
              <div className="hhs-summary-item"><div className="hhs-summary-val" style={{color:"var(--gold)"}}>{pendingC}</div><div className="hhs-summary-lbl">{isRTL?"معلق":"Pending"}</div></div>
              <div className="hhs-summary-item"><div className="hhs-summary-val" style={{color:"var(--blu)"}}>{inProgC}</div><div className="hhs-summary-lbl">{isRTL?"في الطريق":"In Transit"}</div></div>
              <div className="hhs-summary-item"><div className="hhs-summary-val" style={{color:"var(--grn)"}}>{doneC}</div><div className="hhs-summary-lbl">{isRTL?"مكتمل":"Done"}</div></div>
            </div>
            {totalC>0&&<div className="hhs-split-bar">
              {pendingC>0&&<div className="hhs-split-bar-seg" style={{width:`${(pendingC/totalC)*100}%`,background:"var(--gold)"}}/>}
              {inProgC>0&&<div className="hhs-split-bar-seg" style={{width:`${(inProgC/totalC)*100}%`,background:"var(--blu)"}}/>}
              {doneC>0&&<div className="hhs-split-bar-seg" style={{width:`${(doneC/totalC)*100}%`,background:"var(--grn)"}}/>}
            </div>}
          </div>);
        })()}
      </div>

      {/* Mobile: Grocery requests list (one row per request/batch) */}
      <div className="hhs-mobile-list">
        {grocGroups.length>0?<div className="exp-merged-card">
          {grocGroups.map(gr=>{const stColor=gr.overallStatus==="completed"?"var(--grn)":gr.overallStatus==="in_progress"?"var(--blu)":gr.overallStatus==="cancelled"?"var(--g400)":"var(--gold)";const stBg=gr.overallStatus==="completed"?"var(--grn-bg)":gr.overallStatus==="in_progress"?"var(--g50)":gr.overallStatus==="cancelled"?"var(--g100)":"var(--gold-bg)";const stBd=gr.overallStatus==="completed"?"var(--grn-bd)":gr.overallStatus==="in_progress"?"var(--g200)":gr.overallStatus==="cancelled"?"var(--g200)":"var(--gold-bd)";const itemCount=gr.items.length;return(
            <div key={gr.key} className="exp-row" style={{padding:"12px 16px",cursor:"pointer"}} onClick={()=>setGrocDetailKey(gr.key)}>
              <div className="exp-row-info">
                <div className="exp-row-name"><GroceryLabel row={{item:gr.title,itemAr:gr.titleAr}} lang={lang}/></div>
                <div className="exp-row-sub">{itemCount} {isRTL?(itemCount===1?"عنصر":"عناصر"):("item"+(itemCount>1?"s":""))} · {userName(users,gr.requestedBy)}{gr.locId?` · ${locName(locations,gr.locId)}`:""}{gr.date?` · ${gr.date}`:""}</div>
              </div>
              <span className="exp-m-badge" style={{background:stBg,color:stColor,border:`1px solid ${stBd}`}}>{GROC_STATUS_LABEL[gr.overallStatus]||gr.overallStatus}</span>
            </div>);
          })}
        </div>:<div style={{textAlign:"center",padding:"40px 20px",color:"var(--g400)",fontSize:14}}>🛒<br/>{isRTL?"لا طلبات بقالة":"No grocery requests"}</div>}
        {/* Cross-module tasks (housework, maintenance, etc.) now live in
            the unified Execution view and the StaffView task panel. */}
      </div>

      {/* Mobile: FAB */}
      <button className="hhs-fab" onClick={()=>setShowBulkGrocery(true)}>+</button>

      {/* ═══ DESKTOP: Two-column grid ═══ */}
      <div className="exp-3col hhs-desktop-section" style={{position:"relative",zIndex:1}}>
        <div className="exp-3col-center">
          {/* Module title + subtitle */}
          <div style={{padding:"0 24px",marginBottom:6}}>
            <div className="ptitle" style={{fontSize:22}}>{state.platformModuleNames?.Household||t.household}</div>
            <div className="psub">{state.moduleSubtitles?.Household||`${locFilter?locName(locations,locFilter):t.allHomes} · ${t.groceries}`}</div>
          </div>
          {/* Add Groceries */}
          <div style={{padding:"0 24px",marginBottom:14,display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid var(--g100)"}}>
            <div style={{flex:1}}/>
            <button data-onboarding-target="household-add-fab" className="btn btn-p btn-sm" onClick={()=>setShowBulkGrocery(true)} style={{borderRadius:8,padding:"6px 14px",fontSize:12,flexShrink:0,marginBottom:4}}>+ {isRTL?"إضافة بقالة":"Add Groceries"}</button>
          </div>
          {/* Stat strip */}
          <div style={{padding:"0 24px",marginBottom:14}}>
            <div style={{display:"flex",gap:10}}>
              <div style={{flex:1,background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:12,padding:"10px 14px"}}><div style={{fontSize:10,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{isRTL?"الإجمالي":"Total"}</div><div style={{fontFamily:"var(--fm)",fontSize:16,fontWeight:600,color:"var(--ink)"}}>{fg.length}</div></div>
              <div style={{flex:1,background:"var(--gold-bg)",border:"1px solid var(--gold-bd)",borderRadius:12,padding:"10px 14px"}}><div style={{fontSize:10,fontWeight:600,color:"var(--gold)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{isRTL?"معلق":"Pending"}</div><div style={{fontFamily:"var(--fm)",fontSize:16,fontWeight:600,color:"var(--gold)"}}>{fg.filter(g=>getGrocStatus(g)==="pending").length}</div></div>
              <div style={{flex:1,background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:12,padding:"10px 14px"}}><div style={{fontSize:10,fontWeight:600,color:"var(--blu)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{isRTL?"في الطريق":"In Transit"}</div><div style={{fontFamily:"var(--fm)",fontSize:16,fontWeight:600,color:"var(--blu)"}}>{fg.filter(g=>getGrocStatus(g)==="in_progress").length}</div></div>
              <div style={{flex:1,background:"var(--grn-bg)",border:"1px solid var(--grn-bd)",borderRadius:12,padding:"10px 14px"}}><div style={{fontSize:10,fontWeight:600,color:"var(--grn)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{isRTL?"مكتمل":"Fulfilled"}</div><div style={{fontFamily:"var(--fm)",fontSize:16,fontWeight:600,color:"var(--grn)"}}>{fg.filter(g=>getGrocStatus(g)==="completed").length}</div></div>
            </div>
          </div>
          {/* Card-style grocery list */}
          <div className="exp-3col-list-scroll">
            {grocGroups.length>0?<div className="exp-merged-card" style={{borderRadius:16,margin:"0 20px"}}>
              {grocGroups.map(gr=>{const stColor=gr.overallStatus==="completed"?"var(--grn)":gr.overallStatus==="in_progress"?"var(--blu)":gr.overallStatus==="cancelled"?"var(--g400)":"var(--gold)";const stBg=gr.overallStatus==="completed"?"var(--grn-bg)":gr.overallStatus==="in_progress"?"var(--g50)":gr.overallStatus==="cancelled"?"var(--g100)":"var(--gold-bg)";const stBd=gr.overallStatus==="completed"?"var(--grn-bd)":gr.overallStatus==="in_progress"?"var(--g200)":gr.overallStatus==="cancelled"?"var(--g200)":"var(--gold-bd)";const itemCount=gr.items.length;return(
                <div key={gr.key} className="exp-row" style={{padding:"10px 16px",gap:12,cursor:"pointer"}} onClick={()=>setGrocDetailKey(gr.key)}>
                  <div className="exp-row-icon" style={{background:stBg}}><span style={{color:stColor,fontSize:16}}>🛒</span></div>
                  <div className="exp-row-info" style={{minWidth:0}}>
                    <div className="exp-row-name"><GroceryLabel row={{item:gr.title,itemAr:gr.titleAr}} lang={lang}/></div>
                    <div className="exp-row-sub">{itemCount} {isRTL?(itemCount===1?"عنصر":"عناصر"):("item"+(itemCount>1?"s":""))} · {userName(users,gr.requestedBy)}{gr.locId?` · ${locName(locations,gr.locId)}`:""}{gr.date?` · ${gr.date}`:""}{gr.totalCost>0?` · ${fmtSAR(gr.totalCost)}`:""}</div>
                  </div>
                  <span className="exp-m-badge" style={{background:stBg,color:stColor,border:`1px solid ${stBd}`,flexShrink:0}}>{GROC_STATUS_LABEL[gr.overallStatus]||gr.overallStatus}</span>
                </div>);
              })}
            </div>:<div style={{textAlign:"center",padding:"60px 20px",color:"var(--g400)",fontSize:14}}><div style={{fontSize:36,marginBottom:10,opacity:.5}}>🛒</div><div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noGroceryRequests}.</div></div>}
          </div>

          {/* Maid tasks block removed — see Execution / StaffView for tasks */}
        </div>

        {/* ── RIGHT COLUMN: Analytics ── */}
        <div className="exp-3col-right" style={{paddingTop:116}}>
      {(()=>{
        const fulfilled=fg.filter(g=>getGrocStatus(g)==="completed");
        const grocDates=[...new Set(fg.map(g=>g.date).filter(Boolean))].sort();
        const weekCount=grocDates.length>0?Math.max(1,Math.ceil((new Date(grocDates[grocDates.length-1])-new Date(grocDates[0]))/(7*864e5))):1;
        const runsPerWeek=grocDates.length>0?(grocDates.length/weekCount).toFixed(1):"0";
        const bills=fulfilled.map(g=>(g.totalCost||g.cost||0)).filter(b=>b>0);
        const totalBills=bills.length;
        const avgBill=totalBills>0?(bills.reduce((s,b)=>s+b,0)/totalBills).toFixed(0):"—";
        const minBill=totalBills>0?Math.min(...bills):"—";
        const maxBill=totalBills>0?Math.max(...bills):"—";
        const medianBill=(()=>{if(!totalBills)return"—";const sorted=[...bills].sort((a,b)=>a-b);const mid=Math.floor(sorted.length/2);return sorted.length%2?sorted[mid]:Math.round((sorted[mid-1]+sorted[mid])/2);})();
        /* Per location breakdown */
        const locGrocStats={};
        fg.forEach(g=>{
          const lid=g.locId||"unassigned";
          if(!locGrocStats[lid])locGrocStats[lid]={name:lid==="unassigned"?t.unassignedLabel:locName(locations,lid),total:0,fulfilled:0,pending:0,bills:[]};
          locGrocStats[lid].total++;
          const gs2=getGrocStatus(g);
          if(gs2==="completed"){locGrocStats[lid].fulfilled++;if(g.totalCost||g.cost)locGrocStats[lid].bills.push(g.totalCost||g.cost);}
          if(gs2==="pending")locGrocStats[lid].pending++;
        });
        const locGrocArr=Object.values(locGrocStats).sort((a,b)=>b.total-a.total);
        /* SLA analysis on groceries */
        const slaGroceries=fg.filter(g=>g.date&&g.fulfilledDate);
        const slaDays=slaGroceries.map(g=>Math.max(0,Math.ceil((new Date(g.fulfilledDate)-new Date(g.date))/864e5)));
        const avgSla=slaDays.length>0?(slaDays.reduce((s,d)=>s+d,0)/slaDays.length).toFixed(1):"—";
        const minSla=slaDays.length>0?Math.min(...slaDays):"—";
        const maxSla=slaDays.length>0?Math.max(...slaDays):"—";

        return(<>
          {/* Overview card */}
          <div className="exp-3col-right-card">
            <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
              <div style={{fontFamily:"var(--fm)",fontSize:20,fontWeight:700,color:"var(--ink)"}}>{runsPerWeek}</div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{t.runsPerWeek}</div>
            </div>
            <div className="exp-split-row">
              <div className="exp-split-item"><div className="exp-split-dot" style={{background:"var(--ink)"}}/><div className="exp-split-meta"><div className="exp-split-lbl">{t.avgBill}</div><div className="exp-split-val">{avgBill!=="—"?fmtSAR(avgBill):"—"}</div></div></div>
              <div className="exp-split-item"><div className="exp-split-dot" style={{background:"var(--grn)"}}/><div className="exp-split-meta"><div className="exp-split-lbl">{t.minLabel}</div><div className="exp-split-val" style={{color:"var(--grn)"}}>{minBill!=="—"?fmtSAR(minBill):"—"}</div></div></div>
              <div className="exp-split-item"><div className="exp-split-dot" style={{background:"var(--red)"}}/><div className="exp-split-meta"><div className="exp-split-lbl">{t.maxLabel}</div><div className="exp-split-val" style={{color:"var(--red)"}}>{maxBill!=="—"?fmtSAR(maxBill):"—"}</div></div></div>
            </div>
          </div>

          {/* SLA card */}
          <div className="exp-3col-right-card">
            <div className="exp-3col-right-title">{t.fulfillmentSLA}</div>
            <div style={{display:"flex",gap:12}}>
              <div style={{flex:1,textAlign:"center"}}><div style={{fontFamily:"var(--fm)",fontSize:20,fontWeight:700,color:avgSla!=="—"&&parseFloat(avgSla)>3?"var(--red)":"var(--grn)"}}>{avgSla}</div><div style={{fontSize:10,color:"var(--g400)"}}>{t.avgDays}</div></div>
              <div style={{flex:1,textAlign:"center"}}><div style={{fontFamily:"var(--fm)",fontSize:20,fontWeight:700,color:"var(--grn)"}}>{minSla!=="—"?minSla:"—"}</div><div style={{fontSize:10,color:"var(--g400)"}}>{t.fastest}</div></div>
              <div style={{flex:1,textAlign:"center"}}><div style={{fontFamily:"var(--fm)",fontSize:20,fontWeight:700,color:"var(--ink)"}}>{medianBill!=="—"?fmtSAR(medianBill):"—"}</div><div style={{fontSize:10,color:"var(--g400)"}}>{t.medianBill}</div></div>
            </div>
          </div>
            {/* Per location breakdown — only show if workspace has more than 1 location */}
            {locations.length>1&&locGrocArr.length>0&&<div>
              <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>{t.byLocation}</div>
              {locGrocArr.map(loc=>{const pct=loc.total>0?Math.round((loc.fulfilled/loc.total)*100):0;const locAvg=loc.bills.length>0?Math.round(loc.bills.reduce((s,b)=>s+b,0)/loc.bills.length):0;return(
                <div key={loc.name} className="bar-r">
                  <div className="bar-l">{loc.name}</div>
                  <div className="bar-t"><div className="bar-f" style={{width:`${pct}%`,background:pct>=75?"var(--grn)":pct>=50?"var(--amb)":"var(--red)"}}/></div>
                  <div className="bar-v">{loc.fulfilled}/{loc.total} {t.fulfilledSuffix}{locAvg>0?` · ${t.avgBill} ${fmt(locAvg)}`:""}{loc.pending>0?` · ${loc.pending} ${t.pending}`:""}</div>
                </div>
              );})}
              {/* Location comparison stats */}
              {(()=>{
                const withBills=locGrocArr.filter(l=>l.bills.length>0);
                const bestFulfill=locGrocArr.reduce((b,l)=>{const r=l.total>0?l.fulfilled/l.total:0;return r>(b.r||0)?{...l,r}:b;},{r:0,name:"—"});
                const cheapest=withBills.reduce((b,l)=>{const a=l.bills.reduce((s,v)=>s+v,0)/l.bills.length;return a<(b.a||Infinity)?{...l,a}:b;},{a:Infinity,name:"—"});
                const mostExpensive=withBills.reduce((b,l)=>{const a=l.bills.reduce((s,v)=>s+v,0)/l.bills.length;return a>(b.a||0)?{...l,a}:b;},{a:0,name:"—"});
                return withBills.length>1?(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,marginTop:12}}>
                    <div style={{padding:"10px 14px",background:"var(--g50)",borderRadius:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{t.bestFulfillment}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--grn)"}}>{bestFulfill.name} <span style={{fontFamily:"var(--fm)",fontSize:11,fontWeight:400}}>{Math.round(bestFulfill.r*100)}%</span></div>
                    </div>
                    <div style={{padding:"10px 14px",background:"var(--g50)",borderRadius:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{t.lowestAvgBill}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--grn)"}}>{cheapest.name} <span style={{fontFamily:"var(--fm)",fontSize:11,fontWeight:400}}>SAR {fmt(Math.round(cheapest.a))}</span></div>
                    </div>
                    <div style={{padding:"10px 14px",background:"var(--g50)",borderRadius:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{t.highestAvgBill}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--red)"}}>{mostExpensive.name} <span style={{fontFamily:"var(--fm)",fontSize:11,fontWeight:400}}>SAR {fmt(Math.round(mostExpensive.a))}</span></div>
                    </div>
                  </div>
                ):null;
              })()}
            </div>}

          {/* AI Intelligence */}
          <div className="exp-ai-wrap" style={{margin:0}}><AIPanel title={t.intelligence} context={`Groceries: ${fg.length}, Pending: ${fg.filter(g=>getGrocStatus(g)==="pending").length}, Fulfilled: ${fg.filter(g=>getGrocStatus(g)==="completed").length}`} prompt="Household operations insights. Grocery efficiency? Task completion? Cost optimization? 3 insights." lang={lang} moduleKey="Household" wsState={state}/></div>
        </>);
      })()}
        </div>{/* end right column */}
      </div>{/* end 2-col grid */}

      {/* Bulk Grocery Add Modal */}
      {showBulkGrocery&&<Modal><BulkGroceryModal state={state} dispatch={dispatch} t={t} locations={locations} locFilter={locFilter} onClose={()=>setShowBulkGrocery(false)}/></Modal>}

      {/* Grocery Request Detail Sheet */}
      {grocDetailKey&&(()=>{
        const gr=grocGroups.find(g=>g.key===grocDetailKey);
        if(!gr)return null;
        const stColor=gr.overallStatus==="completed"?"var(--grn)":gr.overallStatus==="in_progress"?"var(--blu)":gr.overallStatus==="cancelled"?"var(--g400)":"var(--gold)";
        const stBg=gr.overallStatus==="completed"?"var(--grn-bg)":gr.overallStatus==="in_progress"?"var(--g50)":gr.overallStatus==="cancelled"?"var(--g100)":"var(--gold-bg)";
        const stBd=gr.overallStatus==="completed"?"var(--grn-bd)":gr.overallStatus==="in_progress"?"var(--g200)":gr.overallStatus==="cancelled"?"var(--g200)":"var(--gold-bd)";
        const supplierId=gr.batchTask?.contextPayload?.supplierId;
        const supplierName=supplierId?(state.suppliers||[]).find(s=>s.id===supplierId)?.name||"":"";
        const fulfilledDates=gr.items.map(g=>g.fulfilledDate).filter(Boolean).sort();
        const fulfilledDate=fulfilledDates[fulfilledDates.length-1]||"";
        return(
        <Modal><div className="stf-detail-overlay" onClick={()=>setGrocDetailKey(null)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div>
                <div className="stf-detail-title"><GroceryLabel row={{item:gr.title,itemAr:gr.titleAr}} lang={lang}/></div>
                <div className="stf-detail-sub">{gr.items.length} {isRTL?(gr.items.length===1?"عنصر":"عناصر"):("item"+(gr.items.length>1?"s":""))}{gr.locId?` · ${locName(locations,gr.locId)}`:""}{gr.date?` · ${gr.date}`:""}</div>
              </div>
              <button className="stf-detail-close" onClick={()=>setGrocDetailKey(null)}>✕</button>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",padding:"0 20px 12px"}}>
              <span className="exp-m-badge" style={{background:stBg,color:stColor,border:`1px solid ${stBd}`}}>{GROC_STATUS_LABEL[gr.overallStatus]||gr.overallStatus}</span>
              {gr.doneCount>0&&gr.doneCount<gr.items.length&&<span style={{fontSize:11,color:"var(--g400)"}}>{gr.doneCount}/{gr.items.length} {isRTL?"مكتمل":"fulfilled"}</span>}
            </div>
            <div className="stf-detail-body">
              <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"طلب بواسطة":"Requested By"}</span><span className="stf-detail-row-value">{userName(users,gr.requestedBy)}</span></div>
              {gr.locId&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الموقع":"Location"}</span><span className="stf-detail-row-value">{locName(locations,gr.locId)}</span></div>}
              {gr.date&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"تاريخ الطلب":"Request Date"}</span><span className="stf-detail-row-value">{gr.date}</span></div>}
              {supplierName&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"المورد":"Supplier"}</span><span className="stf-detail-row-value">{supplierName}</span></div>}
              {gr.batchTask?.assignedToName&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"المسؤول":"Assigned To"}</span><span className="stf-detail-row-value">{gr.batchTask.assignedToName}</span></div>}
              {fulfilledDate&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"تاريخ الإكمال":"Fulfilled"}</span><span className="stf-detail-row-value">{fulfilledDate}</span></div>}
              {gr.totalCost>0&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"التكلفة الإجمالية":"Total Cost"}</span><span className="stf-detail-row-value mono" style={{fontWeight:700}}>{fmtSAR(gr.totalCost)}</span></div>}

              <div className="stf-detail-section">{isRTL?"العناصر":"Items"}</div>
              <div style={{border:"1px solid var(--g100)",borderRadius:12,overflow:"hidden",background:"var(--white)"}}>
                {gr.items.map((g,i)=>{
                  const gs=getGrocStatus(g);
                  const next=(GROC_VALID_TRANSITIONS[gs]||[])[0];
                  const iColor=gs==="completed"?"var(--grn)":gs==="in_progress"?"var(--blu)":gs==="cancelled"?"var(--g400)":"var(--gold)";
                  const iBg=gs==="completed"?"var(--grn-bg)":gs==="in_progress"?"var(--g50)":gs==="cancelled"?"var(--g100)":"var(--gold-bg)";
                  const iBd=gs==="completed"?"var(--grn-bd)":gs==="in_progress"?"var(--g200)":gs==="cancelled"?"var(--g200)":"var(--gold-bd)";
                  return(
                  <div key={g.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderBottom:i<gr.items.length-1?"1px solid var(--g100)":"none"}}>
                    <div style={{width:32,height:32,borderRadius:8,background:iBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><span style={{fontSize:15}}>🛒</span></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}><GroceryLabel row={g} lang={lang}/></div>
                      <div style={{fontSize:11,color:"var(--g400)",fontFamily:"var(--fm)"}}>{g.qty}{g.unit?` ${g.unit}`:""}</div>
                    </div>
                    <span className="exp-m-badge" style={{background:iBg,color:iColor,border:`1px solid ${iBd}`,flexShrink:0,minWidth:86,justifyContent:"center",textAlign:"center"}}>{GROC_STATUS_LABEL[gs]||gs}</span>
                    {next&&<button className="btn btn-g btn-xs" style={{flexShrink:0,fontSize:10,padding:"4px 8px",borderRadius:6}} onClick={()=>doGrocTransition(g.id,next)}>{GROC_STATUS_LABEL[next]}</button>}
                  </div>);
                })}
              </div>
            </div>
            <div className="stf-detail-actions">
              <button className="btn btn-g" onClick={()=>setGrocDetailKey(null)}>{isRTL?"إغلاق":"Close"}</button>
            </div>
          </div>
        </div></Modal>);
      })()}

      {/* Grocery Expense Modal */}
      {grocExpModal&&(<Modal>
        <div className="mo" onClick={()=>setGrocExpModal(null)}>
          <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:540}}>
            <div className="mh"><div className="mt">{isRTL?"تسجيل مصروف البقالة":"Log Grocery Run Expense"}</div><button className="btn btn-g btn-sm" onClick={()=>setGrocExpModal(null)}>✕</button></div>
            <div style={{padding:"0 24px 8px",fontSize:13,color:"var(--g500)"}}>{grocExpModal.isLastItem?(isRTL?"هذا آخر عنصر في طلب البقالة. سجّل التكلفة الإجمالية لإكمال الطلب.":"This is the last item in the grocery run. Log the total cost to complete the run."):(isRTL?"يتطلب إكمال هذا الطلب تسجيل المصروف الفعلي.":"Fulfilling this request requires logging the actual expense.")}</div>
            <div style={{padding:"0 24px 8px",fontSize:13,fontWeight:500}}>{grocExpModal.isLastItem?(isRTL?"آخر عنصر":"Last item")+": ":""}{grocExpModal.item}</div>
            <ExpenseForm
              cats={state.expenseCats||[]} locations={locations} users={users||[]} vehicles={state.vehicles||[]} projects={state.projects||[]} staff={state.staff||[]}
              defaultUser={currentUser?.id||"u1"} currentUser={currentUser} defaultCatId={grocConfig.defaultExpenseCatId} defaultSubId={grocConfig.defaultExpenseSubId}
              defaultLocId={(()=>{const g2=groceries.find(x=>x.id===grocExpModal.id);return g2?.locId||"";})()}
              compact
              t={t} lang={lang} wsKey={state.wsKey}
              onCancel={()=>setGrocExpModal(null)}
              onSave={async(expense)=>{
                const g=groceries.find(x=>x.id===grocExpModal.id);
                /* Fulfill last grocery item — API creates the expense record */
                dispatch({type:"UPDATE_GROCERY",id:grocExpModal.id,patch:{status:"completed"}});
                /* Compose a bilingual default note if the user didn't supply
                   one, picking the right side of the bilingual grocery row
                   for each language so an English viewer of the expense
                   list never sees a maid's Arabic-typed item and vice versa. */
                const itemEn=g?.itemEn||(/[؀-ۿ]/.test(g?.item||"")?"":g?.item)||grocExpModal.item;
                const itemAr=g?.itemAr||(/[؀-ۿ]/.test(g?.item||"")?g?.item:"")||"";
                const defaultNote=`Grocery run: ${itemEn||itemAr}`;
                const defaultNoteAr=itemAr?`رحلة بقالة: ${itemAr}`:`رحلة بقالة: ${itemEn}`;
                fulfillWithExpense(grocExpModal.id,{
                  amount:expense.amount,
                  notes:expense.notes||defaultNote,
                  notes_ar:expense.notesAr||(expense.notes?"":defaultNoteAr),
                  loc_id:expense.locId||g?.locId||null,cat_id:expense.catId||null,sub_id:expense.subId||null,
                  invoice_name:expense.invoiceName||null,invoice_url:expense.invoiceUrl||null,
                });
                setGrocExpModal(null);
              }}
            />
          </div>
        </div>
      </Modal>)}

    </div>
  );
}

function BulkGroceryModal({state,dispatch,t,locations,locFilter,onClose}){
  const platformCtx=useContext(PlatformCtx);
  const {lang}=state;
  const isRTL=lang==="ar";
  const vt=useValidationToast();
  const API=typeof window!=="undefined"?window.location.origin:"";
  const emptyRow=()=>({id:`bg${Date.now()}${Math.random().toString(36).slice(2,6)}`,item:"",qty:"1",unit:"",selectedGi:null});
  const [rows,setRows]=useState(()=>Array.from({length:10},emptyRow));
  const [locId,setLocId]=useState(locFilter||locations[0]?.id||"");
  const [supplierId,setSupplierId]=useState("");
  const [saving,setSaving]=useState(false);
  /* Autocomplete state — per row */
  const [acIdx,setAcIdx]=useState(-1);
  const [acResults,setAcResults]=useState([]);
  const [acLoading,setAcLoading]=useState(false);
  const acTimer=useRef(null);

  const hasData=rows.some(r=>r.item.trim());
  const confirmClose=()=>{
    if(hasData){if(!window.confirm(t.discardConfirm))return;}
    onClose();
  };

  const updateRow=(idx,field,val)=>setRows(prev=>prev.map((r,i)=>i===idx?{...r,[field]:val}:r));
  const addMoreRows=()=>setRows(prev=>[...prev,...Array.from({length:5},emptyRow)]);
  const removeRow=(idx)=>{setRows(prev=>prev.length>1?prev.filter((_,i)=>i!==idx):prev);if(acIdx===idx){setAcIdx(-1);setAcResults([]);}};

  /* Autocomplete search — fires on item input change */
  const acSearch=(idx,q)=>{
    if(acTimer.current)clearTimeout(acTimer.current);
    if(!q.trim()){setAcResults([]);setAcIdx(-1);return;}
    setAcIdx(idx);
    acTimer.current=setTimeout(async()=>{
      setAcLoading(true);
      try{
        const r=await authFetch(`${API}/api/grocery-search?q=${encodeURIComponent(q)}&lang=${lang}&limit=8`);
        const j=await r.json();
        setAcResults(j.results||[]);
      }catch{setAcResults([]);}
      setAcLoading(false);
    },300);
  };
  const selectAcItem=(idx,item)=>{
    const name=lang==="ar"&&item.nameAr?item.nameAr:item.nameEn;
    setRows(prev=>prev.map((r,i)=>i===idx?{...r,item:name,unit:item.unit||item.defaultUnit||r.unit,selectedGi:item}:r));
    setAcIdx(-1);setAcResults([]);
    // Log selection
    authFetch(`${API}/api/grocery-search`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:state.wsKey,user_id:state.currentUser?.id,query:rows[idx]?.item,selected_item_id:item.id,selected_layer:item.layer,results_count:acResults.length})}).catch(()=>{});
  };

  const submitAll=async()=>{
    const valid=rows.filter(r=>r.item.trim());
    const m=validateFields([{ok:valid.length,label:isRTL?"عنصر واحد على الأقل":"At least 1 item"},{ok:locId||locFilter||locations[0]?.id,label:isRTL?"الموقع":"Location"}]);
    if(m.length){vt.show(m.join(", "));return;}
    setSaving(true);
    const grocConfig=state.groceryConfig||{};
    const locLabel=locations.find(l=>l.id===locId)?.name||"";
    const batchLocId=locId||locFilter||locations[0]?.id||"";

    /* Step 1: Build grocery rows with canonical bilingual fields.
       For each row determine itemEn/itemAr/itemSource:
         - catalog match  → copy both names from intelligence DB row
         - free-text      → put what user typed into the matching slot
                             based on Arabic-character detection; the
                             counterpart is filled later (best-effort
                             AI translate via /api/ai-chat at write).
       The legacy `item` field is kept populated with whatever the user
       typed so older code paths (CSV exports, expense descriptions
       composed from grocery items) keep working unchanged. */
    const isAr=s=>typeof s==="string"&&/[؀-ۿ]/.test(s);
    const groceryIds=[];
    const items=valid.map(r=>{
      const sel=r.selectedGi;
      const layer=sel?.layer;
      const gId=`g${Date.now()}${Math.random().toString(36).slice(2,6)}`;
      groceryIds.push(gId);
      const typed=r.item.trim();
      let itemEn="",itemAr="",itemSource="user";
      if(sel?.nameEn||sel?.nameAr){
        itemEn=sel.nameEn||"";
        itemAr=sel.nameAr||"";
        itemSource="catalog";
      }else if(isAr(typed)){
        itemAr=typed;
      }else{
        itemEn=typed;
      }
      return {
        id:gId,
        item:typed,                         /* legacy field preserved */
        itemEn,itemAr,itemSource,
        qty:parseInt(r.qty)||1,unit:r.unit||sel?.unit||sel?.defaultUnit||"",status:"pending",
        requestedBy:state.currentUser?.id||"u3",locId:batchLocId,date:TODAY,
        productId:layer==="product"?sel?.id:null,
        variantId:layer==="variant"?sel?.id:(sel?.variantId||null),
        genericItemId:layer==="generic"?sel?.id:(sel?.genericItemId||null),
      };
    });
    items.forEach(gItem=>dispatch({type:"ADD_GROCERY",item:gItem}));

    /* Step 2: Create ONE task for the entire grocery run */
    if(grocConfig.autoCreateTask!==false){
      try{
        const grocSlaConfig={};
        if(grocConfig.slaDays)grocSlaConfig.expected_completion_days=parseFloat(grocConfig.slaDays);
        if(grocConfig.slaWarningPercent)grocSlaConfig.warning_threshold_percent=parseInt(grocConfig.slaWarningPercent);
        /* Resolve default assignee: workspace override → platform default */
        const platGrocDefaults=(platformCtx?.platformGroceryConfig)||{};
        const assigneeRole=grocConfig.defaultAssigneeRole||platGrocDefaults.defaultAssigneeRole||"";
        const assigneeUser=assigneeRole?(state.users||[]).find(u=>u.active!==false&&u.role===assigneeRole):null;
        /* Bilingual task title: stored as both English (canonical) and
           Arabic so viewers see it in their language regardless of which
           language the staff member who created the run was using. */
        const locObj=locations.find(l=>l.id===batchLocId);
        const locLabelAr=locObj?.nameAr||locObj?.name||locLabel;
        const taskTitle=`Grocery Run — ${items.length} item${items.length===1?"":"s"}${locLabel?` — ${locLabel}`:""}`;
        const taskTitleAr=`طلب بقالة — ${items.length} ${items.length===1?"عنصر":"عناصر"}${locLabelAr?` — ${locLabelAr}`:""}`;
        /* Phase 2.5: resolve taxonomy entry id for "grocery_run". */
        const _hhTax=getEffectiveTaxonomy(state,platformCtx,"Household");
        const _hhEntry=_hhTax.find(x=>x.value==="grocery_run")||_hhTax[0]||null;
        const taskTypeId=_hhEntry?_hhEntry.id:null;
        const res=await authFetch(`${API}/api/tasks?action=create_task`,{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ws_key:state.wsKey,actor_id:state.currentUser?.id,actor_name:state.currentUser?.name,
            task:{title:taskTitle, title_ar:taskTitleAr,
              description:"",
              priority:grocConfig.defaultPriority||platGrocDefaults.defaultPriority||"medium",created_by:state.currentUser?.id||null,
              assigned_to:assigneeUser?.id||null,
              assigned_to_name:assigneeUser?.name||"",
              source_module:"Household",task_type:"grocery_run",
              /* Legacy task_type string kept so cascade logic in
                 api/tasks.js:57 keeps matching. */
              task_type_id:taskTypeId,
              loc_id:batchLocId,
              context_payload:{groceryIds,itemCount:items.length,supplierId:supplierId||null,items:items.map(i=>({id:i.id,item:i.item,qty:i.qty,unit:i.unit})),taxonomyId:taskTypeId},
              linked_entities:groceryIds.map(id=>({type:"grocery",id})),
              requires_approval:grocConfig.requireApproval||false,
              requires_expense:grocConfig.requireExpense||false,
              approval_roles:grocConfig.approvalRoles||["owner"],
              sla_config:Object.keys(grocSlaConfig).length?grocSlaConfig:undefined}})});
        const data=await res.json();
        if(data.ok&&data.task){
          /* Link all grocery items to this single task */
          groceryIds.forEach(gId=>dispatch({type:"UPDATE_GROCERY",id:gId,patch:{taskId:data.task.id}}));
          dispatch({type:"ADD_TASK",task:data.task});
        }
      }catch(e){console.error("[Grocery] task creation failed",e);}
    }
    setSaving(false);
    onClose();
  };

  const layerColors={product:"#2563eb",variant:"#7c3aed",generic:"var(--g400)"};
  const layerLabels={product:t.product,variant:t.variant,generic:t.generic};

  return(
    <div className="stf-detail-overlay" onClick={confirmClose}>
      {vt.Toast}
      <div className="stf-detail-sheet" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
        <div className="stf-detail-handle"/>
        <div className="stf-detail-header">
          <div className="stf-detail-title">{isRTL?"إضافة بقالة":"Add Groceries"}</div>
          <button className="stf-detail-close" onClick={confirmClose}>✕</button>
        </div>
        <div className="stf-detail-body">
          {/* Location + Supplier — top selectors */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",marginBottom:4}}>{isRTL?"الموقع":"Location"}</div>
              <select className="fs" value={locId} onChange={e=>setLocId(e.target.value)} style={{width:"100%",fontSize:13,padding:"10px 12px"}}>
                {locations.map(l=><option key={l.id} value={l.id}>{isRTL&&l.nameAr?l.nameAr:l.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",marginBottom:4}}>{isRTL?"المورد":"Supplier"} <span style={{fontWeight:400,color:"var(--g300)"}}>({isRTL?"اختياري":"optional"})</span></div>
              <select className="fs" value={supplierId} onChange={e=>setSupplierId(e.target.value)} style={{width:"100%",fontSize:13,padding:"10px 12px"}}>
                <option value="">{isRTL?"بدون":"None"}</option>
                {(state.suppliers||[]).filter(s=>(s.status||"active")==="active"&&(!s.module||s.module==="grocery"||s.module==="general")).map(s=><option key={s.id} value={s.id}>{isRTL&&s.nameAr?s.nameAr:s.name}</option>)}
              </select>
            </div>
          </div>
          {/* Column headers */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 56px 72px 28px",gap:6,alignItems:"center",marginBottom:6,padding:"0 2px"}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)"}}>{t.item}</div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)"}}>{t.qty}</div>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)"}}>{t.unit}</div>
            <div/>
          </div>
          {rows.map((row,idx)=>(
            <div key={row.id} style={{display:"grid",gridTemplateColumns:"1fr 56px 72px 28px",gap:6,alignItems:"center",marginBottom:6}}>
              <div style={{position:"relative"}}>
                <input className="fi" value={row.item} onChange={e=>{updateRow(idx,"item",e.target.value);setRows(prev=>prev.map((r,i)=>i===idx?{...r,selectedGi:null}:r));acSearch(idx,e.target.value);}} placeholder={`${t.item} ${idx+1}`} style={{fontSize:13,padding:"8px 10px",width:"100%"}} onFocus={()=>{if(row.item.trim()&&acResults.length&&acIdx===idx)setAcIdx(idx);}} onBlur={()=>setTimeout(()=>{if(acIdx===idx){setAcIdx(-1);setAcResults([]);}},200)} onKeyDown={e=>{if(e.key==="Enter"&&acIdx===idx&&acResults.length)selectAcItem(idx,acResults[0]);if(e.key==="Escape"){setAcIdx(-1);setAcResults([]);}}}/>
                {acIdx===idx&&acResults.length>0&&(
                  <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:999,background:"var(--g0,#fff)",border:"1px solid var(--g100)",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,.12)",maxHeight:240,overflowY:"auto"}}>
                    {acResults.map((r,i)=>(
                      <div key={r.id+r.layer} style={{padding:"7px 10px",cursor:"pointer",borderBottom:i<acResults.length-1?"1px solid var(--g50)":"none",display:"flex",alignItems:"center",gap:6}} onMouseDown={()=>selectAcItem(idx,r)} onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:500}}>{lang==="ar"&&r.nameAr?r.nameAr:r.nameEn}</div>
                          <div style={{fontSize:10,color:"var(--g400)",display:"flex",gap:4,alignItems:"center",marginTop:1}}>
                            {r.brand&&<span>{r.brand}</span>}
                            {r.size&&<span>{r.size}</span>}
                            {r.nameAr&&lang!=="ar"&&<span dir="rtl">{r.nameAr}</span>}
                          </div>
                        </div>
                        {(r.unit||r.defaultUnit)&&<span style={{fontSize:9,padding:"1px 5px",background:"var(--g50)",borderRadius:4,color:"var(--g400)"}}>{r.unit||r.defaultUnit}</span>}
                        <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,fontWeight:600,letterSpacing:".03em",textTransform:"uppercase",color:layerColors[r.layer]||"var(--g400)",background:(layerColors[r.layer]||"var(--g400)")+"14",border:"1px solid "+(layerColors[r.layer]||"var(--g400)")+"30"}}>{layerLabels[r.layer]||r.layer}</span>
                        {r.isPinned&&<span style={{fontSize:9}}>📌</span>}
                      </div>
                    ))}
                    {acLoading&&<div style={{padding:"5px 10px",fontSize:10,color:"var(--g400)"}}>{isRTL?"جارٍ البحث...":"Searching..."}</div>}
                  </div>
                )}
              </div>
              <input className="fi" type="number" min="1" value={row.qty} onChange={e=>updateRow(idx,"qty",e.target.value)} style={{fontSize:13,padding:"8px 6px",textAlign:"center"}}/>
              <input className="fi" value={row.unit} onChange={e=>updateRow(idx,"unit",e.target.value)} placeholder={t.unitPh} style={{fontSize:13,padding:"8px 6px"}}/>
              <button className="btn btn-g btn-sm" onClick={()=>removeRow(idx)} style={{padding:"4px 6px",fontSize:12,lineHeight:1}} title={t.remove}>✕</button>
            </div>
          ))}
          <button className="btn btn-g btn-sm" onClick={addMoreRows} style={{marginTop:8,width:"100%"}}>{t.addMoreRows}</button>
        </div>
        <div style={{padding:"12px 20px 24px",display:"flex",gap:8,alignItems:"center"}}>
          <div style={{fontSize:12,color:"var(--g400)",flex:1,minWidth:0}}>{rows.filter(r=>r.item.trim()).length} {t.itemsReady}</div>
          <button className="btn btn-g" style={{borderRadius:14,padding:"14px 16px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={confirmClose}>{t.cancel}</button>
          <button className="btn btn-p" style={{borderRadius:14,padding:"14px 16px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={submitAll} disabled={saving||!rows.some(r=>r.item.trim())}>{saving?t.addingInProgress:t.addAll}</button>
        </div>
      </div>
    </div>
  );
}

export { HouseholdGroceriesTab, BulkGroceryModal };
export default HouseholdOps;
